/**
 * MoveFormModal.tsx
 *
 * Moves a completed form (a surveys_responses row) from one patient's chart to
 * another — for a form that was filled out under the wrong patient. Opened from
 * SurveyResponseModal's footer when FormsTab hosts it.
 *
 * Flow: search for the destination (the source patient is filtered out), show
 * both patients side by side the way PatientMergeModal does, then confirm.
 * The server writes a chart note on each patient; notes previously copied from
 * the form with "Send to Notes" are not moved, which is what the one-line
 * warning tells the user.
 *
 * Errors: the move endpoint answers refusals with `{ error: { code, message } }`
 * (409/422) and a message written for staff, so it is shown as-is via
 * getErrorMessage. Those statuses are not ones apiClient redirects on.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Spinner } from '@/components/ui';
import { PatientSearchInput } from '@/components/medical/PatientSearchInput';
import { formatDate } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import { getPatient } from '@/services/patients.service';
import { moveSurveyResponse } from '@/services/patientForms.service';
import { toast } from '@/store/toastStore';
import type { PatientSummary } from '@/types/patient.types';

interface MoveFormModalProps {
  open: boolean;
  onClose: () => void;
  fromPatientId: number;
  responseId: number | null;
  /** Called after a successful move so the host can close the form it came from. */
  onMoved: () => void;
}

const MOVE_FAILED_FALLBACK =
  'The form could not be moved. Please try again, and if it keeps happening, contact your administrator.';

function fullName(p: { firstName?: string | null; lastName?: string | null; displayName?: string | null }): string {
  const name = `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim();
  return name || (p.displayName ?? '').trim() || 'this patient';
}

function PatientCard({ label, name, dob, loading }: { label: string; name: string | null; dob: string | null; loading?: boolean }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      {loading ? (
        <div className="py-2"><Spinner /></div>
      ) : (
        <>
          <div className="text-sm font-medium text-slate-900">{name}</div>
          <div className="mt-0.5 text-sm text-slate-600">
            {formatDate(dob) === '—' ? 'Date of birth unknown' : `Date of birth ${formatDate(dob)}`}
          </div>
        </>
      )}
    </div>
  );
}

export function MoveFormModal({ open, onClose, fromPatientId, responseId, onMoved }: MoveFormModalProps) {
  const queryClient = useQueryClient();
  const [destination, setDestination] = useState<PatientSummary | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // ConfirmDialog closes itself once onConfirm resolves. On a refused move we
  // swallow the error (so it is not an unhandled rejection) and set this flag,
  // so the close it then requests is ignored and the message stays on screen.
  const keepConfirmOpen = useRef(false);

  useEffect(() => {
    if (!open) {
      setDestination(null);
      setConfirmOpen(false);
      setError(null);
    }
  }, [open]);

  const { data: source, isLoading: sourceLoading } = useQuery({
    queryKey: ['move-form-source-patient', fromPatientId],
    queryFn: () => getPatient(fromPatientId),
    enabled: open,
    staleTime: 60_000,
  });

  const destinationName = destination ? fullName(destination) : '';

  const handleConfirm = async () => {
    if (!destination || responseId === null) return;
    setError(null);
    try {
      await moveSurveyResponse(fromPatientId, responseId, destination.patientId);
    } catch (err) {
      setError(getErrorMessage(err, MOVE_FAILED_FALLBACK));
      keepConfirmOpen.current = true;
      return;
    }
    const to = destination.patientId;
    for (const key of [
      ['patient-forms-list', fromPatientId],
      ['patient-forms-list', to],
      ['patient-forms', fromPatientId],
      ['patient-forms', to],
      ['forms-inbox'],
      // The server leaves a chart note on both patients recording the move.
      ['patient-notes'],
    ]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
    toast.success(`Form moved to ${destinationName}'s chart.`);
    setConfirmOpen(false);
    onMoved();
  };

  const handleConfirmClose = () => {
    if (keepConfirmOpen.current) {
      keepConfirmOpen.current = false;
      return;
    }
    setConfirmOpen(false);
    setError(null);
  };

  const footer = (
    <div className="flex w-full items-center justify-end gap-3">
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="primary" onClick={() => setConfirmOpen(true)} disabled={!destination || responseId === null}>
        Move form
      </Button>
    </div>
  );

  return (
    <>
      <Modal isOpen={open} onClose={onClose} title="Move form to another patient" size="lg" footer={footer}>
        <div className="space-y-5">
          <PatientSearchInput
            onSelect={setDestination}
            selectedPatient={destination}
            placeholder="Search by name, email, or phone…"
            excludePatientId={fromPatientId}
          />

          {destination && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <PatientCard
                  label="Current patient"
                  name={source ? fullName(source) : null}
                  dob={source?.dateOfBirth ?? null}
                  loading={sourceLoading}
                />
                <PatientCard label="Move to" name={destinationName} dob={destination.dateOfBirth} />
              </div>
              <p className="text-sm text-slate-600">
                Notes already copied from this form stay on the current patient&apos;s chart.
              </p>
            </div>
          )}
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={handleConfirmClose}
        onConfirm={handleConfirm}
        variant="warning"
        title="Move this form?"
        message={
          <>
            {`This form will be moved to ${destinationName}'s chart.`}
            {error && (
              <span role="alert" className="mt-2 block text-red-600">
                {error}
              </span>
            )}
          </>
        }
        confirmLabel="Yes, move it"
        cancelLabel="Go back"
      />
    </>
  );
}
